export type ThingWithId = {id?: string; [key: string]: unknown}

/* ************************************** CUSTOM TYPE GUARDS ************************************** */
export function hasProp(obj: unknown, prop: string): obj is {[key: string]: unknown} {
  return typeof obj === 'object' && obj !== null && !Array.isArray(obj) && prop in obj
}

export function isSubclassOf(
  obj: any,
  superClass: new (...args: any[]) => any
): obj is typeof superClass {
  return obj.prototype instanceof superClass
}

// JsonData is "anything you could serialize to/deserialize from JSON."

// That means it contains no functions, no class instances, and it does not recursively reference
// itself.

// It can include primitives (strings, numbers, booleans, nulls, undefined), and arrays and objects
// built out of primitives.
export type JsonPrimitive = string | number | bigint | boolean | null | undefined
export type JsonObject = {
  [key: string]: JsonPrimitive | JsonObject | JsonData[]
}
export type JsonData = JsonPrimitive | JsonObject | JsonData[]

export interface JsonObjectWithId extends JsonObject {
  id: string
}

export function isJsonData(obj: unknown, origin?: unknown, propChain?: string): obj is JsonData {
  if (
    typeof obj === 'string' ||
    typeof obj === 'number' ||
    typeof obj === 'boolean' ||
    obj === null ||
    obj === undefined
  ) {
    return true
  }
  if (obj === origin) {
    console.log(`Recursion detected on ${obj} at ${propChain}.`)
    return false
  }
  if (Array.isArray(obj)) {
    for (const item of obj) {
      if (!isJsonData(item, origin ?? obj, propChain)) {
        return false
      }
    }
    return true
  } else if (typeof obj === 'object') {
    for (const prop in obj) {
      if (
        !isJsonData(
          (obj as Record<string, unknown>)[prop],
          origin ?? obj,
          propChain ? `${propChain}.${prop}` : prop
        )
      ) {
        return false
      }
    }
    return true
  }
  console.log(`Non-JSON-compatible object detected: ${obj}`)
  return false
}

export function jsonDataIsObj(obj: JsonData): obj is JsonObject {
  return typeof obj === 'object' && obj !== null
}
export function jsonDataIsObjWithProp(
  obj: JsonData,
  prop: string,
  errorOnFail?: boolean
): obj is JsonObject {
  const output = typeof obj === 'object' && obj !== null && prop in obj
  if (!output && errorOnFail) {
    throw new Error(`"${prop}" property nonexistent in:\n${JSON.stringify(obj)}`)
  }
  return output
}
export function jsonDataIsObjWithId(obj: JsonData, errorOnFail?: boolean): obj is JsonObjectWithId {
  const output = jsonDataIsObjWithProp(obj, 'id') && typeof obj.id === 'string'
  if (!output && errorOnFail) {
    throw new Error(
      `Invalid "id" property (expected string, got ${typeof (obj as JsonObject)
        .id}) in:\n${JSON.stringify(obj)}`
    )
  }
  return output
}
export function jsonDataIsArrayWithIds(
  obj: JsonData,
  errorOnFail?: boolean
): obj is JsonObjectWithId[] {
  if (!Array.isArray(obj)) {
    if (errorOnFail) {
      throw new Error(`Expected array of objects with Id property, got:\n${JSON.stringify(obj)}`)
    }
    return false
  }
  for (const item of obj) {
    if (!jsonDataIsObjWithId(item, errorOnFail)) {
      return false
    }
  }
  return true
}

export function jsonDataGet(obj: JsonObject, prop: string, opts?: {errorOnFail?: boolean}) {
  const {errorOnFail = true} = opts ?? {}
  if (prop === '' || prop.split('.').length === 0) {
    throw new Error(`Invalid property name: "${prop}"`)
  }
  let output: JsonData = obj
  for (const subprop of prop.split('.')) {
    if (!jsonDataIsObj(output) || !(subprop in output)) {
      if (errorOnFail) {
        throw new Error(
          `Unable to retrieve "${prop}" from data (failed on "${subprop}"):\n${JSON.stringify(obj)}`
        )
      }
      return undefined
    }
    output = output[subprop]
  }
  return output
}

export function jsonDataSet(
  obj: JsonObject,
  prop: string,
  val: JsonData,
  opts?: {errorOnFail?: boolean; requirePathExists?: boolean}
) {
  const {errorOnFail = true, requirePathExists = true} = opts ?? {}
  let target: JsonData = obj
  const subprops = prop.split('.')
  const finalprop = subprops.pop()
  if (prop === '' || finalprop === undefined) {
    throw new Error(`Invalid property name: "${prop}"`)
  }
  const fail = (subprop: string) => {
    if (errorOnFail) {
      throw new Error(
        `Unable to modify "${prop}" in data (failed on "${subprop}"):\n${JSON.stringify(obj)}`
      )
    }
    return false
  }
  for (const subprop of subprops) {
    if (!jsonDataIsObj(target)) {
      return fail(subprop)
    }
    if (!(subprop in target)) {
      if (requirePathExists) {
        return fail(subprop)
      }
      target[subprop] = {}
    }
    target = target[subprop]
  }
  if (!jsonDataIsObj(target)) {
    if (errorOnFail) {
      return fail(subprops.slice(-1)[0])
    }
    return false
  }
  if (!(finalprop in target) && requirePathExists) {
    return fail(finalprop)
  }
  target[finalprop] = val
  return true
}

export function cloneJson<JType extends JsonData>(data: JType): JType {
  if (Array.isArray(data)) {
    return data.map((x) => cloneJson(x)) as JType
  } else if (jsonDataIsObj(data)) {
    const output: JsonObject = {}
    for (const prop in data) {
      output[prop] = cloneJson(data[prop])
    }
    return output as JType
  } else {
    return data
  }
}

export function mergeJsonArray<JType extends JsonData>(
  base: JType[],
  updates: JType[],
  propChain?: string[]
) {
  if (jsonDataIsArrayWithIds(base) && (base.length > 0 || jsonDataIsArrayWithIds(updates))) {
    if (!jsonDataIsArrayWithIds(updates)) {
      throw new Error(
        `Unable to merge values at ${(propChain ?? []).join(
          '.'
        )}: Expected array of objects with ids, got: ${JSON.stringify(updates)}`
      )
    }
    const baseById: Record<string, JType & JsonObjectWithId> = {}
    for (const item of base) {
      baseById[item.id] = item
    }
    const output: JType[] = []
    for (const item of updates) {
      if (item.id in baseById) {
        output.push(mergeJsonObject(baseById[item.id], item))
      } else {
        output.push(cloneJson(item))
      }
    }
    return output
  }

  const output: JType[] = []
  for (let i = 0; i < updates.length; i++) {
    if (i < base.length) {
      output.push(mergeJson(base[i], updates[i], propChain))
    } else {
      output.push(cloneJson(updates[i]))
    }
  }
  return output
}

export function mergeJsonObject<JType extends JsonObject>(
  base: JType,
  updates: Partial<JType>,
  propChain?: string[]
) {
  const output: JsonObject = {}
  for (const prop in base) {
    output[prop] = mergeJson(base[prop], updates[prop], propChain ? [...propChain, prop] : [prop])
  }
  for (const prop in updates) {
    if (!(prop in base)) {
      output[prop] = cloneJson(updates[prop])
    }
  }
  return output as JType
}

export function mergeJson<JType extends JsonData>(
  base: JType | undefined,
  updates: JType | undefined,
  propChain?: string[]
): JType {
  if (base === undefined) {
    if (updates === undefined) {
      return undefined as JType
    }
    return cloneJson(updates)
  }
  if (updates === undefined) {
    return cloneJson(base)
  }
  if (Array.isArray(base)) {
    if (!Array.isArray(updates)) {
      throw new Error(
        `Unable to merge values at ${(propChain ?? []).join(
          '.'
        )}: Expected array, got: ${JSON.stringify(updates)}`
      )
    }
    return mergeJsonArray(base, updates, propChain) as JType
  }
  if (jsonDataIsObj(base)) {
    if (!jsonDataIsObj(updates)) {
      throw new Error(
        `Unable to merge values at ${(propChain ?? []).join(
          '.'
        )}: Expected object, got: ${JSON.stringify(updates)}`
      )
    }
    return mergeJsonObject(base, updates, propChain) as JType
  }
  return updates
}
